import React, { useCallback, useEffect, useState, type PropsWithChildren } from 'react';
import { styled } from '@compiled/react';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { DRAWER_Z_INDEX } from '../../common/constants.tsx';
import messages from './messages.tsx';

type DrawerProps = PropsWithChildren<{
	isOpen: boolean;
	onClose: VoidFunction;
}>;

const BANNER_ID = 'page-layout.banner';
const OLD_NAV_HEIGHT = 48;
const NEW_NAV_HEIGHT = 56;

export const Drawer = ({ isOpen, onClose, children }: DrawerProps) => {
	const { formatMessage } = useIntl();
	const [modalTopOffset, setModalTopOffset] = useState<number | undefined>(undefined);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape' && isOpen) {
				onClose();
			}
		},
		[isOpen, onClose],
	);

	useEffect(() => {
		if (isOpen) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.addEventListener('keydown', handleKeyDown);
		} else {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.removeEventListener('keydown', handleKeyDown);
		}

		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, handleKeyDown]);

	useEffect(() => {
		if (!fg('polaris_roadmap_banner_fix')) {
			return;
		}

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const banner = document.getElementById(BANNER_ID);

		const updateBannerHeight = () => {
			const bannerHeight = banner?.getBoundingClientRect().height ?? 0;
			const navHeight = getWillShowNav4() ? OLD_NAV_HEIGHT : NEW_NAV_HEIGHT;

			setModalTopOffset(banner ? bannerHeight + navHeight : undefined);
		};

		setTimeout(updateBannerHeight, 1000);

		const observer = new MutationObserver(() => updateBannerHeight());
		const bannerParent = banner?.parentNode;
		if (bannerParent) {
			// so that we can adjust the drawer height when the banner is dismissed
			observer.observe(bannerParent, { childList: true, subtree: true });
		}
		return () => {
			if (bannerParent) {
				observer.disconnect();
			}
		};
	}, []);

	return (
		<ExitingPersistence appear>
			{isOpen && (
				<SlideIn duration="medium" enterFrom="left" exitTo="left">
					{(props) => (
						<Container {...props} topOffset={modalTopOffset}>
							{children}
							<Box xcss={closeButtonStyles}>
								<IconButton
									shape="circle"
									icon={CrossIcon}
									label={formatMessage(messages.closeButtonLabel)}
									appearance="subtle"
									onClick={onClose}
								/>
							</Box>
						</Container>
					)}
				</SlideIn>
			)}
		</ExitingPersistence>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{ topOffset?: number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	height: ({ topOffset }) =>
		topOffset ? `calc(100vh - ${topOffset}px)` : 'calc(100vh - var(--topNavigationHeight))',
	width: '100vw',
	position: 'fixed',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	top: ({ topOffset }) => (topOffset ? `${topOffset}px` : 'var(--topNavigationHeight)'),
	left: token('space.0'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: DRAWER_Z_INDEX,
	backgroundColor: token('elevation.surface.overlay'),
	overflow: 'hidden',
});

const closeButtonStyles = xcss({
	position: 'absolute',
	top: 'space.250',
	left: 'space.250',
});
